/**
 * Support Request API response parsers
 * Transform API responses to app-friendly format
 * Defensive parsing: optional chaining + null checks to prevent UI crashes
 */

/**
 * Safe date formatter — returns empty string on any failure
 * @param {*} dateString
 * @returns {string}
 */
const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');

    return `${day} ${month} ${year}, ${displayHours}:${displayMinutes} ${ampm}`;
  } catch {
    return '';
  }
};

/**
 * Map API status string to display label
 * Unknown statuses fall through as-is instead of breaking
 * @param {string} status
 * @returns {string}
 */
const mapStatus = (status) => {
  if (!status || typeof status !== 'string') return '';
  const statusMap = {
    OPENED: 'Pending',
    IN_PROGRESS: 'In Progress',
    RESOLVED: 'Solved',
    CLOSED: 'Solved',
  };
  return statusMap[status] ?? status;
};

/**
 * Check if a response code indicates success.
 * Handles number 200, string '200', or API-specific codes.
 * @param {string|number} code
 * @param {string[]} [extraCodes] - additional success codes to accept
 * @returns {boolean}
 */
const isSuccessCode = (code, extraCodes = []) => {
  if (code == null) return false;
  const normalized = String(code);
  return normalized === '200' || extraCodes.includes(normalized);
};

// ---------------------------------------------------------------------------
// Parsers
// ---------------------------------------------------------------------------

/**
 * Parse create support request response
 * @param {*} response - Raw API response
 * @returns {Object}
 */
export const parseCreateSupportRequestResponse = (response) => {
  const defaultError = {
    success: false,
    message: 'Invalid response format',
    code: null,
    id: null,
    data: null,
  };

  if (!response || typeof response !== 'object') return defaultError;

  const code = response?.code ?? null;
  const message = response?.message ?? 'Support request created successfully';
  const result = response?.result ?? null;

  return {
    success: isSuccessCode(code),
    code,
    message,
    id: result?.id ?? null,
    data: result ?? null,
  };
};

/**
 * Parse support requests list response
 * @param {*} response - Raw API response
 * @returns {Object}
 */
export const parseSupportRequestsListResponse = (response) => {
  const defaultError = {
    success: false,
    message: 'Invalid response format',
    code: null,
    requests: [],
    pagination: { page: 1, limit: 20, total: 0 },
    data: null,
  };

  if (!response || typeof response !== 'object') return defaultError;

  const code = response?.code ?? null;
  const message = response?.message ?? 'Support requests retrieved successfully';
  const result = response?.result ?? null;

  return {
    success: isSuccessCode(code),
    code,
    message,
    requests: Array.isArray(result?.data) ? result.data : [],
    pagination: {
      page: result?.page ?? 1,
      limit: result?.limit ?? 20,
      total: result?.total ?? 0,
    },
    data: result ?? null,
  };
};

/**
 * Parse support list response (for /api/support endpoint)
 * Each item is safely mapped — a bad item is skipped, not a crash
 * @param {*} response - Raw API response
 * @returns {Object}
 */
export const parseSupportListResponse = (response) => {
  const defaultError = {
    success: false,
    message: 'Invalid response format',
    code: null,
    data: [],
    pagination: { page: 1, limit: 10, total: 0, totalPages: 0 },
    requestId: null,
    rawData: [],
  };

  if (!response || typeof response !== 'object') return defaultError;

  const code = response?.code ?? null;
  const message = response?.message ?? 'Support requests retrieved successfully';
  const result = response?.result ?? null;
  const requestId = response?.requestId ?? null;

  const rawList = Array.isArray(result?.data) ? result.data : [];

  const formattedData = rawList
    .map((item) => {
      // Skip entirely non-object items to avoid downstream crashes
      if (!item || typeof item !== 'object') return null;

      return {
        id: item?.id ?? null,
        timeRaised: formatDate(item?.created_at),
        type: item?.reason ?? '',
        query: item?.request ?? '',
        ticketId: item?.ticket_ref_id ?? '',
        status: mapStatus(item?.status),
        originalData: item,
      };
    })
    .filter(Boolean); // Remove any nulls from skipped items

  const pagination = result?.pagination ?? {};

  return {
    success: isSuccessCode(code, ['SUPS0001']),
    code,
    message,
    data: formattedData,
    pagination: {
      page: pagination?.page ?? 1,
      limit: pagination?.limit ?? 10,
      total: pagination?.total ?? 0,
      totalPages: pagination?.totalPages ?? 0,
    },
    requestId,
    rawData: rawList,
  };
};

/**
 * Parse support error response
 * @param {*} error - Raw error (axios error, fetch error, or plain object)
 * @returns {Object}
 */
export const parseSupportErrorResponse = (error) => {
  const defaultError = {
    success: false,
    message: 'Unknown error occurred',
    code: 'UNKNOWN_ERROR',
    details: null,
    status: null,
  };

  if (!error || typeof error !== 'object') return defaultError;

  const data = error?.data ?? null;
  const status = error?.status ?? null;

  // Prefer structured API error shape
  if (data?.code) {
    return {
      success: false,
      code: data?.code ?? 'UNKNOWN_ERROR',
      message: data?.message ?? 'An error occurred',
      details: data?.details ?? null,
      status,
    };
  }

  return {
    success: false,
    message: data?.message ?? data?.error ?? 'An error occurred',
    code: data?.code ?? (status != null ? `HTTP_${status}` : 'UNKNOWN_ERROR'),
    details: data?.details ?? null,
    status,
  };
};